import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiInfo, FiX, FiCalendar } from 'react-icons/fi';
import './../Styles/Admin.css';
import './../Styles/EmployeeDetailsDialog.css';
import { useToast } from './ToastContext';
import { TailSpin } from 'react-loader-spinner';
import CustomSelect from './CustomSelect';

const apiUrl = process.env.REACT_APP_API_URL;

const RATING_OPTIONS = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' }
];

const EmployeeDetailsDialog = ({ 
    employee, 
    selectedQuarter, 
    reviewerRole = 'admin', 
    readOnly = false, 
    hideLeadComment = false,
    hideLeadRating = false,
    onClose 
}) => {
    const isReviewedView = Boolean(readOnly || selectedQuarter);
    const shouldHideLeadRating = Boolean(hideLeadRating || reviewerRole === 'hr');
    const shouldHideLeadComment = Boolean(hideLeadComment || reviewerRole === 'hr');
    const showToast = useToast();
    const [employeeDetails, setEmployeeDetails] = useState([]);
    const [infoGoal, setInfoGoal] = useState(null);
    const [finalRating, setFinalRating] = useState('');
    const [finalRatingError, setFinalRatingError] = useState(null);
    const [reviewerCommentError, setReviewerCommentError] = useState(null);
    const [managerCommentError, setManagerCommentError] = useState(null);
    const [noGoalsMessage, setNoGoalsMessage] = useState('');
    const [quarters, setQuarters] = useState([]);
    const [currentQuarter, setCurrentQuarter] = useState(selectedQuarter || '');
    const [isFetchingDetails, setIsFetchingDetails] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isReviewSubmitted, setIsReviewSubmitted] = useState(false);
    const [isManagerRatingOpen, setIsManagerRatingOpen] = useState(false);

    // Fetch quarters from DB
    useEffect(() => {
        if (isReviewedView) return;
        axios.get(`${apiUrl}/api/quarters`)
            .then(res => {
                let qs = res.data.map(q => typeof q === 'string' ? q : (q.quarter || q.quarter_name || q.name || ''));
                if (selectedQuarter && !qs.includes(selectedQuarter)) {
                    qs = [selectedQuarter, ...qs];
                }
                setQuarters(qs);
                if (!currentQuarter && qs.length > 0) {
                    setCurrentQuarter(qs[0]);
                }
            })
            .catch(err => console.error('Error fetching quarters:', err));
    }, [selectedQuarter]);

    useEffect(() => {
        if (selectedQuarter) {
            setCurrentQuarter(selectedQuarter);
        }
    }, [selectedQuarter]);

    // Lock body scroll and listen for Escape key
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (infoGoal) {
                    setInfoGoal(null);
                } else if (onClose) {
                    onClose();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [infoGoal, onClose]);

    // Fetch goals whenever employee or quarter changes
    useEffect(() => {
        if (employee && currentQuarter) {
            fetchEmployeeGoals(employee.employee_number, currentQuarter);
        }
    }, [employee, currentQuarter]);

    const fetchEmployeeGoals = async (employeeNumber, quarter) => {
        setIsFetchingDetails(true);
        setEmployeeDetails([]);
        setNoGoalsMessage('');
        setFinalRating('');
        setFinalRatingError(null);
        setReviewerCommentError(null);
        setManagerCommentError(null);
        setIsReviewSubmitted(false);

        try {
            const response = await axios.get(`${apiUrl}/api/employee-admin-goals/${employeeNumber}/${encodeURIComponent(quarter)}`);
            if (!response.data || response.data.length === 0) {
                setNoGoalsMessage(`No goals submitted for ${employeeNumber} in ${quarter}.`);
                setEmployeeDetails([]);
            } else {
                setEmployeeDetails(response.data);
                setNoGoalsMessage('');
                if (response.data[0]?.final_rating) {
                    setFinalRating(response.data[0].final_rating.toString());
                }
                if (response.data[0]?.is_review_submitted) {
                    setIsReviewSubmitted(true);
                }
            }
        } catch (error) {
            if (error.response && error.response.status === 404) {
                setEmployeeDetails([]);
                setNoGoalsMessage(`No goals submitted for ${employeeNumber} in ${quarter}.`);
            } else {
                console.error('Error fetching employee details:', error);
            }
        } finally {
            setIsFetchingDetails(false);
        }
    };

    const handleReviewerRatingChange = (targetId, newRating) => {
        setIsReviewSubmitted(false);
        setEmployeeDetails(prevDetails =>
            prevDetails.map(detail => {
                const match = 
                    (detail._id && String(detail._id) === String(targetId)) ||
                    (detail.id && String(detail.id) === String(targetId)) ||
                    (detail.goal_id && String(detail.goal_id) === String(targetId));
                return match 
                    ? { ...detail, reviewer_rating: newRating, reviewerRatingError: newRating ? null : detail.reviewerRatingError } 
                    : detail;
            })
        );
    };

    const handleFinalRatingChange = (rating) => {
        setFinalRating(rating);
        setIsReviewSubmitted(false);
        if (rating) {
            setFinalRatingError(null);
        }
    };

    const handleSubmit = async () => {
        if (!employee) return;
        setLoading(true);

        let hasError = false;

        // Validation for Lead reviewer role
        if (reviewerRole === 'lead') {
            const updatedDetails = employeeDetails.map((detail) => {
                const upd = { ...detail };
                if (!upd.reviewer_rating) {
                    upd.reviewerRatingError = 'Reviewer rating is required';
                    hasError = true;
                } else {
                    upd.reviewerRatingError = null;
                }
                return upd;
            });

            if (!employeeDetails[0]?.reviewer_comment) {
                setReviewerCommentError('*Lead Comment is required');
                hasError = true;
            } else {
                setReviewerCommentError(null);
            }

            if (hasError) {
                setEmployeeDetails(updatedDetails);
                setLoading(false);
                return;
            }

            if (isReviewSubmitted) {
                showToast('No changes detected. Review is already submitted.', 'warning');
                setLoading(false);
                return;
            }

            try {
                const leadId = localStorage.getItem('employeeId');
                const goalReviewsData = {
                    quarter: currentQuarter,
                    employeeId: employee.employee_number,
                    goals: employeeDetails.map(detail => ({
                        id: detail._id || detail.id,
                        goalId: detail.goal_id,
                        reviewerRating: detail.reviewer_rating,
                    })),
                };
                await axios.post(`${apiUrl}/api/submit-goal-reviews`, goalReviewsData);

                const finalReviewData = {
                    employeeId: employee.employee_number,
                    leadId: leadId,
                    reviewerComment: employeeDetails[0]?.reviewer_comment,
                    quarter: currentQuarter
                };
                await axios.post(`${apiUrl}/api/notify-manager-review`, finalReviewData);

                setIsReviewSubmitted(true);
                setLoading(false);
                showToast('Submitted successfully!', 'success');
                onClose();
            } catch (err) {
                console.error('Error submitting data:', err);
                setLoading(false);
                showToast('Error while submitting data. Please try again.', 'error');
            }
            return;
        }

        // Validation for Manager / Main Manager / Admin roles
        const isEmployeeRole = employee?.role === 'employee';
        const isLeadRole = employee?.role === 'lead';

        // Validate final rating
        if (!finalRating) {
            setFinalRatingError('*Required');
            hasError = true;
        } else {
            setFinalRatingError(null);
        }

        // Validate comments based on reviewer role and evaluated employee role
        if (reviewerRole === 'main-manager' && isEmployeeRole) {
            if (!employeeDetails[0]?.reviewer_comment) {
                setReviewerCommentError('*Lead Comment is required');
                hasError = true;
            } else {
                setReviewerCommentError(null);
            }
            if (!employeeDetails[0]?.manager_comment) {
                setManagerCommentError('*Manager Comment is required');
                hasError = true;
            } else {
                setManagerCommentError(null);
            }
        } else {
            // Admin or Main Manager reviewing a Lead
            const commentValue = isLeadRole 
                ? employeeDetails[0]?.reviewer_comment 
                : (employeeDetails[0]?.manager_comment || employeeDetails[0]?.reviewer_comment);

            if (!commentValue) {
                setReviewerCommentError('*Manager Comment is required');
                hasError = true;
            } else {
                setReviewerCommentError(null);
            }
        }

        if (hasError) {
            setLoading(false);
            return;
        }

        if (isReviewSubmitted) {
            showToast('No changes detected. Review is already submitted.', 'warning');
            setLoading(false);
            return;
        }

        try {
            // Submit individual goal reviews if any reviewer ratings exist
            const goalReviewsData = {
                quarter: currentQuarter,
                employeeId: employee.employee_number,
                goals: employeeDetails.map(detail => ({
                    id: detail._id || detail.id,
                    goalId: detail.goal_id,
                    reviewerRating: detail.reviewer_rating,
                })),
            };
            await axios.post(`${apiUrl}/api/submit-goal-reviews`, goalReviewsData);

            // Submit final review
            const finalReviewData = {
                employeeId: employee.employee_number,
                finalRating,
                reviewerComment: employeeDetails[0]?.reviewer_comment,
                managerComment: employeeDetails[0]?.manager_comment || employeeDetails[0]?.reviewer_comment,
                quarter: currentQuarter
            };
            await axios.post(`${apiUrl}/api/submit-final-review`, finalReviewData);

            setIsReviewSubmitted(true);
            setLoading(false);
            showToast('Submitted successfully!', 'success');
            onClose();
        } catch (error) {
            console.error('Error submitting review:', error);
            setLoading(false);
            showToast('Error while submitting review. Please try again.', 'error');
        }
    };

    return (
        <div className="details-dialog-overlay" onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}>
            <div className="details-dialog" role="dialog" aria-modal="true" aria-labelledby="dialog-title">
                {/* Header with Title, Role Badge, and 'X' close button */}
                <div className="details-dialog-header">
                    <div className="details-dialog-title-group">
                        <h2 id="dialog-title">Details of {employee.full_name}</h2>
                        <span className="details-dialog-role-badge">
                            {employee.role === 'lead' ? 'Team Lead' : employee.role === 'admin' ? 'Manager' : (employee.job_title || 'Employee')}
                        </span>
                    </div>
                    <button
                        type="button"
                        className="details-dialog-close-btn"
                        onClick={onClose}
                        title="Close (Esc)"
                        aria-label="Close dialog"
                    >
                        <FiX size={20} />
                    </button>
                </div>

                {/* Scrollable Dialog Body */}
                <div className="details-dialog-body">
                    {isReviewedView ? (
                        <div className="quarter-selector quarter-selector-static">
                            <span className="summary-label" style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>Quarter:</span>
                            <span className="quarter-badge-static">
                                <FiCalendar size={13} style={{ marginRight: '6px', verticalAlign: '-1px' }} />
                                {currentQuarter || selectedQuarter}
                            </span>
                        </div>
                    ) : (
                        <div className="quarter-selector">
                            <label htmlFor="quarter">Select Quarter:</label>
                            <CustomSelect
                                value={currentQuarter}
                                onChange={(val) => setCurrentQuarter(val)}
                                options={quarters.map(q => ({ value: q, label: q }))}
                                placeholder="Select Quarter"
                                style={{ minWidth: '170px' }}
                            />
                        </div>
                    )}

                    <div className="employee-summary">
                        <div className="summary-item">
                            <span className="summary-label">Full Name</span>
                            <span className="summary-val">{employee.full_name}</span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Location</span>
                            <span className="summary-val">{employee.location || 'N/A'}</span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Job Title</span>
                            <span className="summary-val">{employee.job_title || 'N/A'}</span>
                        </div>
                        {employee.department && (
                            <div className="summary-item">
                                <span className="summary-label">Department</span>
                                <span className="summary-val">{employee.department}</span>
                            </div>
                        )}
                        {employee.project_name && (
                            <div className="summary-item">
                                <span className="summary-label">Project</span>
                                <span className="summary-val">{employee.project_name}</span>
                            </div>
                        )}
                    </div>

                {employeeDetails.length > 0 ? (
                    <>
                        <div className="details-table-wrapper">
                            <table className="details-table" style={{ opacity: isFetchingDetails ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                                <thead>
                                    <tr>
                                        <th>Goal Description</th>
                                        <th>Self Assessment</th>
                                        <th>Self Rating</th>
                                        <th>Communicated</th>
                                        <th>Attachments</th>
                                        {reviewerRole === 'lead' && !isReviewedView ? (
                                            <th>Lead Rating <span style={{ color: 'red' }}>*</span></th>
                                        ) : (
                                            <>
                                                {employee?.role !== 'lead' && !shouldHideLeadRating && <th>Lead Rating</th>}
                                                <th>
                                                    Manager Rating {!isReviewedView && <span style={{ color: 'red' }}>*</span>}
                                                </th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {employeeDetails.map((detail, index) => (
                                        <tr key={detail.id || detail.goal_id || detail._id || index}>
                                            <td className="tabledisabled">
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                    <span>{detail.goal_name || detail.goal_description}</span>
                                                    <button
                                                        type="button"
                                                        className="goal-info-btn"
                                                        onClick={() => setInfoGoal(detail)}
                                                        title="View Goal Details"
                                                        aria-label="View Goal Details"
                                                    >
                                                        <FiInfo size={15} />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="tabledisabled">{detail.self_assessment || 'N/A'}</td>
                                            <td className="tabledisabled">{detail.self_rating || 'N/A'}</td>
                                            <td className="tabledisabled">{detail.communicated ? 'Yes' : 'No'}</td>
                                            <td className="tabledisabled">
                                                {detail.attachments ? (
                                                    <a href={detail.attachments} target="_blank" className="attachment-link" rel="noopener noreferrer">
                                                        View Attachment
                                                    </a>
                                                ) : 'None'}
                                            </td>
                                             {/* Lead Rating Column */}
                                            {reviewerRole === 'lead' && !isReviewedView ? (
                                                <td>
                                                    <CustomSelect
                                                        value={detail.reviewer_rating !== undefined && detail.reviewer_rating !== null ? String(detail.reviewer_rating) : ''}
                                                        onChange={(val) => handleReviewerRatingChange(detail._id || detail.goal_id, val)}
                                                        options={RATING_OPTIONS}
                                                        placeholder="Select"
                                                        error={!!detail.reviewerRatingError}
                                                    />
                                                    {detail.reviewerRatingError && (
                                                        <div className="inline-error">{detail.reviewerRatingError}</div>
                                                    )}
                                                </td>
                                            ) : (
                                                <>
                                                    {employee?.role !== 'lead' && !shouldHideLeadRating && (
                                                        <td className="tabledisabled">{detail.reviewer_rating ? `${detail.reviewer_rating} / 5` : 'N/A'}</td>
                                                    )}
                                                    {index === 0 && (
                                                        <td 
                                                            rowSpan={employeeDetails.length}
                                                            className={`manager-rating-cell ${isManagerRatingOpen ? 'has-open-select' : ''}`}
                                                            style={{
                                                                position: 'relative',
                                                                zIndex: isManagerRatingOpen ? 1050 : 2,
                                                                verticalAlign: 'top',
                                                                minWidth: '130px'
                                                            }}
                                                        >
                                                            {isReviewedView ? (
                                                                <div className="tabledisabled" style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>
                                                                    {finalRating ? `${finalRating} / 5` : 'Not Assigned'}
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <CustomSelect
                                                                        value={finalRating || ''}
                                                                        onChange={(val) => handleFinalRatingChange(val)}
                                                                        onOpenChange={setIsManagerRatingOpen}
                                                                        options={RATING_OPTIONS}
                                                                        placeholder="Select"
                                                                        error={!!finalRatingError}
                                                                    />
                                                                    {finalRatingError && (
                                                                        <div className="inline-error">{finalRatingError}</div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </td>
                                                    )}
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Reviewer Comments Section */}
                        {isReviewedView ? (
                            <div className="reviewer-comment-view-box">
                                {!shouldHideLeadComment && employeeDetails[0]?.reviewer_comment && (
                                    <div className="comment-view-item">
                                        <strong className="comment-label">Lead Comment:</strong>
                                        <p className="comment-view-text">{employeeDetails[0].reviewer_comment}</p>
                                    </div>
                                )}
                                {employeeDetails[0]?.manager_comment && (
                                    <div className="comment-view-item">
                                        <strong className="comment-label">Manager Comment:</strong>
                                        <p className="comment-view-text">{employeeDetails[0].manager_comment}</p>
                                    </div>
                                )}
                                {(shouldHideLeadComment ? !employeeDetails[0]?.manager_comment : (!employeeDetails[0]?.reviewer_comment && !employeeDetails[0]?.manager_comment)) && (
                                    <div className="comment-view-item">
                                        <strong className="comment-label">Comments:</strong>
                                        <p className="comment-view-text text-muted" style={{ fontStyle: 'italic' }}>No comments recorded for this review.</p>
                                    </div>
                                )}
                            </div>
                        ) : reviewerRole === 'lead' ? (
                            <div className="reviewer-comment-section">
                                <strong className="comment-label">Lead Comment <span style={{ color: 'red' }}>*</span>:</strong>
                                <textarea
                                    value={employeeDetails[0]?.reviewer_comment || ''}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setIsReviewSubmitted(false);
                                        if (val.trim()) setReviewerCommentError(null);
                                        setEmployeeDetails(prevDetails => {
                                            const updated = [...prevDetails];
                                            updated[0].reviewer_comment = val;
                                            return updated;
                                        });
                                    }}
                                    placeholder="Enter lead review comments..."
                                />
                                {reviewerCommentError && (
                                    <div className="inline-error">{reviewerCommentError}</div>
                                )}
                            </div>
                        ) : reviewerRole === 'main-manager' && employee?.role === 'employee' ? (
                            <div className="comments-grid">
                                <div className="reviewer-comment-section">
                                    <strong className="comment-label">Lead Comment <span style={{ color: 'red' }}>*</span>:</strong>
                                    <textarea
                                        value={employeeDetails[0]?.reviewer_comment || ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setIsReviewSubmitted(false);
                                            if (val.trim()) setReviewerCommentError(null);
                                            setEmployeeDetails(prevDetails => {
                                                const updated = [...prevDetails];
                                                updated[0].reviewer_comment = val;
                                                return updated;
                                            });
                                        }}
                                        placeholder="Lead review comment..."
                                    />
                                    {reviewerCommentError && (
                                        <div className="inline-error">{reviewerCommentError}</div>
                                    )}
                                </div>
                                <div className="reviewer-comment-section">
                                    <strong className="comment-label">Manager Comment <span style={{ color: 'red' }}>*</span>:</strong>
                                    <textarea
                                        value={employeeDetails[0]?.manager_comment || ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setIsReviewSubmitted(false);
                                            if (val.trim()) setManagerCommentError(null);
                                            setEmployeeDetails(prevDetails => {
                                                const updated = [...prevDetails];
                                                updated[0].manager_comment = val;
                                                return updated;
                                            });
                                        }}
                                        placeholder="Manager review comment..."
                                    />
                                    {managerCommentError && (
                                        <div className="inline-error">{managerCommentError}</div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="reviewer-comment-section">
                                <strong className="comment-label">Manager Comment <span style={{ color: 'red' }}>*</span>:</strong>
                                <textarea
                                    value={employeeDetails[0]?.manager_comment || employeeDetails[0]?.reviewer_comment || ''}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setIsReviewSubmitted(false);
                                        if (val.trim()) setReviewerCommentError(null);
                                        setEmployeeDetails(prevDetails => {
                                            const updated = [...prevDetails];
                                            updated[0].reviewer_comment = val;
                                            updated[0].manager_comment = val;
                                            return updated;
                                        });
                                    }}
                                    placeholder="Enter manager review comments..."
                                />
                                {reviewerCommentError && (
                                    <div className="inline-error">{reviewerCommentError}</div>
                                )}
                            </div>
                        )}
                    </>
                ) : isFetchingDetails ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px' }}>
                        <TailSpin height="30" width="30" color="#00BFFF" />
                        <span style={{ marginLeft: '10px' }}>Loading goals...</span>
                    </div>
                ) : (
                    <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px 0' }}>
                        {noGoalsMessage || 'No goals found for this employee.'}
                    </p>
                )}
                </div> {/* End details-dialog-body */}

                {/* Footer pinned at bottom - hidden in reviewed report view */}
                {!isReviewedView && (
                    <div className="details-dialog-footer button-group">
                        <button type="button" className="close-button details-dialog-btn-secondary" onClick={onClose}>
                            Close
                        </button>
                        {employeeDetails.length > 0 && (
                            <button
                                type="button"
                                className="sort-button details-dialog-btn-primary"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? 'Submitting...' : 'Submit'}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Goal Information Pop-up Modal */}
            {infoGoal && (
                <div className="pms-overlay modal-overlay" onClick={() => setInfoGoal(null)}>
                    <div 
                        className="pms-modal modal-content goal-info-modal" 
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                            maxWidth: '520px', 
                            width: '92%', 
                            padding: '24px 28px', 
                            borderRadius: '10px',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1px solid var(--border, #e2e8f0)', paddingBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'rgba(234, 88, 12, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ea580c' }}>
                                    <FiInfo size={18} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary, #0f172a)' }}>
                                    Goal Information
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => setInfoGoal(null)}
                                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-secondary, #64748b)', padding: '4px', lineHeight: 1 }}
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-secondary, #64748b)', marginBottom: '6px' }}>
                                Goal Name
                            </label>
                            <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary, #1e293b)', padding: '10px 14px', backgroundColor: 'var(--bg-secondary, #f8fafc)', borderRadius: '6px', border: '1px solid var(--border, #e2e8f0)' }}>
                                {infoGoal.goal_name || infoGoal.goal_description || 'Goal'}
                            </div>
                        </div>

                        <div style={{ marginBottom: '22px' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-secondary, #64748b)', marginBottom: '6px' }}>
                                Goal Description
                            </label>
                            <div style={{ 
                                fontSize: '14px', 
                                lineHeight: '1.6', 
                                color: 'var(--text-primary, #334155)', 
                                padding: '12px 14px', 
                                backgroundColor: 'var(--bg-secondary, #f8fafc)', 
                                borderRadius: '6px', 
                                border: '1px solid var(--border, #e2e8f0)',
                                maxHeight: '220px',
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {infoGoal.description ? (
                                    infoGoal.description
                                ) : (
                                    <span style={{ color: 'var(--text-muted, #94a3b8)', fontStyle: 'italic' }}>
                                        No detailed description provided for this goal.
                                    </span>
                                )}
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                className="pms-btn"
                                onClick={() => setInfoGoal(null)}
                                style={{ 
                                    padding: '8px 24px', 
                                    backgroundColor: '#e67320', 
                                    borderColor: '#e67320', 
                                    color: '#ffffff', 
                                    borderRadius: '6px', 
                                    fontWeight: '600', 
                                    cursor: 'pointer' 
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeDetailsDialog;
